import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ShieldCheck, KeyRound, Trash2, History, Puzzle, Plus, Check, Clock, ShoppingCart, Copy, Infinity as InfinityIcon } from "lucide-react";

const BUY_URL = "https://www.scalionexus.com";
const buyPlanUrl = (plan) => `${BUY_URL}/?plan=${encodeURIComponent(plan || "")}&ref=trial`;

const EDITION_STYLE = {
  pro: "bg-emerald-100 text-emerald-700 ring-emerald-300",
  community: "bg-amber-100 text-amber-700 ring-amber-300",
};

function AddonCard({ addon, onRemove, editable, t }) {
  const seatLabel = addon.kind === "admins" ? t("license.addonAdmins")
    : addon.kind === "users" ? t("license.addonUsers") : null;
  const isTrial = !!addon.trial;
  const daysLeft = addon.daysLeft;
  return (
    <div className={`flex items-start justify-between gap-3 rounded-sm border px-3 py-2.5 ${isTrial ? "border-indigo-200 bg-indigo-50/40" : "border-zinc-200 bg-white"}`} data-testid={`addon-card-${addon.ref}`}>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-zinc-800">{addon.name}</span>
          {isTrial ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-indigo-700" data-testid={`addon-trial-badge-${addon.ref}`}>
              <Clock size={11} /> {t("license.trialBadge")}
            </span>
          ) : (
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${addon.unique ? "bg-violet-100 text-violet-700" : "bg-sky-100 text-sky-700"}`}>
              {addon.unique ? t("license.addonUnique") : t("license.addonCumulative")}
            </span>
          )}
          {addon.demo && (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-700" data-testid={`addon-demo-badge-${addon.ref}`}>
              {t("license.demoBadge")}
            </span>
          )}
        </div>
        <p className="mt-0.5 text-xs text-zinc-500">
          {isTrial && addon.trialMode === "plan"
            ? <span className="inline-flex items-center gap-1 text-indigo-600"><Check size={12} /> {t("license.trialPlanGrant")} <b className="font-semibold">{addon.plan}</b></span>
            : addon.kind === "feature"
              ? <span className="inline-flex items-center gap-1 text-emerald-600"><Check size={12} /> {t("license.addonFeatureOn")}</span>
              : addon.seats > 0 ? `+${addon.seats} ${seatLabel}` : t("license.addonNoSeats")}
          {isTrial
            ? <span className="ml-1 font-medium text-indigo-600">· {daysLeft != null && daysLeft >= 0 ? t("license.trialDaysLeft").replace("{n}", daysLeft) : t("license.trialEnded")} ({addon.expiresAt})</span>
            : (addon.expiresAt ? ` · ${t("license.expires")} ${addon.expiresAt}` : "")}
        </p>
      </div>
      {editable && (
        <button onClick={() => onRemove(addon.ref)} data-testid={`addon-remove-${addon.ref}`}
          className="shrink-0 text-zinc-400 hover:text-red-600" title={t("license.remove")}>
          <Trash2 size={15} />
        </button>
      )}
    </div>
  );
}

function UsageBar({ label, used, max, testid }) {
  const unlimited = max === null || max === undefined;
  const pct = unlimited ? 0 : Math.min(100, Math.round((used / Math.max(1, max)) * 100));
  const over = !unlimited && used >= max;
  return (
    <div data-testid={testid}>
      <div className="mb-1 flex items-center justify-between text-xs">
        <span className="font-semibold text-zinc-600">{label}</span>
        <span className={`font-mono ${over ? "text-red-600" : "text-zinc-500"}`}>
          {used} / {unlimited ? <InfinityIcon size={12} className="inline" /> : max}
        </span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-zinc-200">
        <div className={`h-full ${over ? "bg-red-500" : unlimited ? "bg-emerald-400" : "bg-primary"}`}
          style={{ width: unlimited ? "100%" : `${pct}%` }} />
      </div>
    </div>
  );
}

export const LicensePanel = () => {
  const { t } = useLang();
  const { license, refreshLicense, demoMode } = useAuth();
  const [keyInput, setKeyInput] = useState("");
  const [addonInput, setAddonInput] = useState("");
  const [addonBusy, setAddonBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [policy, setPolicy] = useState(null); // {graceDays, reminderDays}
  const [savingPolicy, setSavingPolicy] = useState(false);

  useEffect(() => { refreshLicense(); }, [refreshLicense]);
  useEffect(() => {
    api.get("/settings").then((r) => setPolicy({
      graceDays: r.data.licenseBuildGraceDays ?? 7,
      reminderDays: r.data.licenseExpiryReminderDays ?? 14,
    })).catch(() => {});
  }, []);

  const savePolicy = async () => {
    setSavingPolicy(true);
    try {
      await api.put("/settings", {
        licenseBuildGraceDays: Number(policy.graceDays),
        licenseExpiryReminderDays: Number(policy.reminderDays),
      });
      toast.success(t("license.activated"));
      refreshLicense();
    } catch (e) { toast.error(apiErr(e)); } finally { setSavingPolicy(false); }
  };

  const lic = license || {};
  const usage = lic.usage || { employees: 0, admins: 0, consoleUsers: 0 };
  const managed = !!lic.managed;
  const isPro = lic.edition === "pro";
  const editable = !managed && !demoMode;

  const copyInfraId = async () => {
    try {
      await navigator.clipboard.writeText(lic.infrastructureId);
      toast.success(t("license.infraIdCopied"));
    } catch { toast.error(t("license.infraIdCopyFailed")); }
  };

  const activate = async () => {
    setBusy(true);
    try {
      await api.post("/license/activate", { key: keyInput.trim() });
      toast.success(t("license.activated"));
      setKeyInput("");
      refreshLicense();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const removeKey = async () => {
    setBusy(true);
    try {
      await api.delete("/license");
      toast.success(t("license.removed"));
      refreshLicense();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const activateAddon = async () => {
    setAddonBusy(true);
    try {
      await api.post("/license/addon", { key: addonInput.trim() });
      toast.success(t("license.addonActivated"));
      setAddonInput("");
      refreshLicense();
    } catch (e) { toast.error(apiErr(e)); } finally { setAddonBusy(false); }
  };

  const removeAddon = async (ref) => {
    setAddonBusy(true);
    try {
      await api.delete(`/license/addon?ref=${encodeURIComponent(ref)}`);
      toast.success(t("license.addonRemoved"));
      refreshLicense();
    } catch (e) { toast.error(apiErr(e)); } finally { setAddonBusy(false); }
  };

  const addons = lic.addons || [];
  const addonEditable = !demoMode && !lic.saas;
  const planTrial = addons.find((a) => a.trial && a.trialMode === "plan" && a.valid);
  const endedPlanTrial = addons.find((a) => a.trial && a.trialMode === "plan" && a.valid === false);

  return (
    <section className="border border-zinc-200 bg-white" data-testid="license-panel">
      <div className="border-b border-zinc-100 p-5">
        <div className="flex items-center gap-2 text-sm font-bold text-zinc-900">
          <ShieldCheck size={16} className="text-primary" /> {t("license.title")}
        </div>
        <p className="mt-1 text-sm text-zinc-500">{t("license.subtitle")}</p>
      </div>

      <div className="grid grid-cols-1 gap-6 p-5 md:grid-cols-2">
        {/* Status */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <span data-testid="license-edition"
              className={`rounded-sm px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-[0.1em] whitespace-nowrap ring-1 ${EDITION_STYLE[isPro ? "pro" : "community"]}`}>
              {isPro ? (lic.plan || "pro") : "Free"}
            </span>
            {lic.customer && <span className="text-sm font-semibold text-zinc-800">{lic.customer}</span>}
          </div>
          <dl className="space-y-1.5 text-sm">
            <div className="flex justify-between"><dt className="text-zinc-400">{t("license.plan")}</dt><dd className="font-medium text-zinc-700">{lic.plan || "community"}</dd></div>
            <div className="flex justify-between"><dt className="text-zinc-400">{t("license.status")}</dt>
              <dd className={`font-medium ${isPro ? "text-emerald-600" : "text-amber-600"}`}>{isPro ? t("license.active") : t("license.limited")}</dd></div>
            <div className="flex justify-between"><dt className="text-zinc-400">{t("license.expires")}</dt><dd className="font-medium text-zinc-700">{lic.expiresAt || t("license.never")}</dd></div>
            {!managed && lic.boundTenant !== undefined && (
              <div className="flex justify-between"><dt className="text-zinc-400">{t("license.boundTenant")}</dt>
                <dd className="font-mono text-xs text-zinc-600" data-testid="license-bound-tenant">{lic.boundTenant || t("license.anyTenant")}</dd></div>
            )}
            {lic.infrastructureId && (
              <div className="flex items-center justify-between"><dt className="text-zinc-400">{t("license.infrastructureId")}</dt>
                <dd className="flex items-center gap-1.5">
                  <span className="font-mono text-xs text-zinc-600" data-testid="license-infrastructure-id">{lic.infrastructureId}</span>
                  <button type="button" onClick={copyInfraId} data-testid="license-infra-id-copy"
                    title={t("license.infraIdCopy")} aria-label={t("license.infraIdCopy")}
                    className="text-zinc-400 transition-colors hover:text-primary">
                    <Copy size={13} />
                  </button>
                </dd></div>
            )}
            {lic.saas && (
              <div className="flex justify-between"><dt className="text-zinc-400">{t("license.buildDate")}</dt>
                <dd className="font-mono text-xs text-zinc-600" data-testid="license-build-date">{lic.buildDate || t("license.buildDateDev")}</dd></div>
            )}
            {!managed && (lic.buildBlocked || lic.buildGrace || lic.buildDate) && (
              <div className="flex justify-between"><dt className="text-zinc-400">{t("license.buildCoverage")}</dt>
                <dd data-testid="license-build-coverage"
                  className={`font-medium ${lic.buildBlocked ? "text-red-600" : lic.buildGrace ? "text-amber-600" : "text-emerald-600"}`}>
                  {lic.buildBlocked ? t("license.blocked")
                    : lic.buildGrace ? `${t("license.grace")} · ${t("license.graceUntil")} ${lic.buildGraceUntil}`
                    : t("license.covered")}
                </dd></div>
            )}
            <div className="flex justify-between"><dt className="text-zinc-400">{t("license.source")}</dt><dd className="font-mono text-xs text-zinc-500">{lic.source || "none"}</dd></div>
          </dl>
          <Link to="/actions?category=license" data-testid="license-history-link"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline">
            <History size={13} /> {t("license.viewHistory")}
          </Link>
          {planTrial && (
            <div className="rounded-sm border border-indigo-200 bg-indigo-50 px-3 py-2.5 text-xs text-indigo-800" data-testid="license-trial-notice">
              <div className="flex items-start gap-2">
                <Clock size={14} className="mt-0.5 shrink-0" />
                <span>
                  {t("license.trialActive").replace("{plan}", planTrial.plan || "")}
                  {planTrial.daysLeft != null && ` · ${t("license.trialDaysLeft").replace("{n}", Math.max(0, planTrial.daysLeft))}`}
                  {planTrial.expiresAt && ` (${planTrial.expiresAt})`}
                </span>
              </div>
              {planTrial.daysLeft != null && planTrial.daysLeft <= 7 && (
                <p className="mt-1 pl-6 font-semibold text-indigo-900" data-testid="license-trial-expiring">{t("license.trialExpiringSoon")}</p>
              )}
              <a href={buyPlanUrl(planTrial.plan)} target="_blank" rel="noopener noreferrer" data-testid="license-buy-plan"
                className="mt-2 ml-6 inline-flex items-center gap-1.5 rounded-sm bg-indigo-600 px-3 py-1.5 text-xs font-bold text-white transition-colors hover:bg-indigo-700">
                <ShoppingCart size={13} /> {t("license.buyPlan").replace("{plan}", planTrial.plan || "")}
              </a>
            </div>
          )}
          {!planTrial && endedPlanTrial && (
            <div className="rounded-sm border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800" data-testid="license-trial-ended-notice">
              <p>{t("license.trialEndedNotice").replace("{plan}", endedPlanTrial.name || endedPlanTrial.plan || "")}</p>
              <a href={buyPlanUrl(endedPlanTrial.plan || endedPlanTrial.name)} target="_blank" rel="noopener noreferrer" data-testid="license-buy-plan-ended"
                className="mt-2 inline-flex items-center gap-1.5 rounded-sm bg-indigo-600 px-3 py-1.5 text-xs font-bold text-white transition-colors hover:bg-indigo-700">
                <ShoppingCart size={13} /> {t("license.buyPlan").replace("{plan}", endedPlanTrial.name || endedPlanTrial.plan || "")}
              </a>
            </div>
          )}
          {lic.error && <div className="rounded-sm border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700" data-testid="license-error">{lic.error}</div>}
        </div>

        {/* Usage */}
        <div className="space-y-4 rounded-sm border border-zinc-100 bg-zinc-50 p-4">
          <div className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("license.seats")}</div>
          <UsageBar label={t("license.employees")} used={usage.employees} max={lic.maxEmployees} testid="usage-employees" />
          {lic.addonEmployees > 0 && (
            <p className="-mt-2 text-[11px] text-sky-600" data-testid="usage-employees-addon">
              {t("license.base")} {lic.baseMaxEmployees ?? "∞"} + {lic.addonEmployees} {t("license.fromAddons")}
            </p>
          )}
          <UsageBar label={t("license.admins")} used={usage.admins} max={lic.maxAdmins} testid="usage-admins" />
          {lic.addonAdmins > 0 && (
            <p className="-mt-2 text-[11px] text-sky-600" data-testid="usage-admins-addon">
              {t("license.base")} {lic.baseMaxAdmins ?? "∞"} + {lic.addonAdmins} {t("license.fromAddons")}
            </p>
          )}
        </div>
      </div>

      {/* Add-ons */}
      {(!demoMode || addons.length > 0) && (
        <div className="border-t border-zinc-100 p-5" data-testid="license-addons">
          <div className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-zinc-500">
            <Puzzle size={14} /> {t("license.addonsTitle")}
          </div>
          {addons.length > 0 ? (
            <div className="mb-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
              {addons.map((a) => (
                a.valid === false
                  ? (a.trial
                      ? <div key={a.ref} className="flex items-center justify-between gap-2 rounded-sm border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700" data-testid={`addon-trial-ended-${a.ref}`}>
                          <span className="inline-flex items-center gap-1.5"><Clock size={13} /> {t("license.trialBadge")} — {a.name}: {t("license.trialEnded")} ({a.expiresAt})</span>
                          {addonEditable && <button onClick={() => removeAddon(a.ref)} className="underline" data-testid={`addon-remove-${a.ref}`}>{t("license.remove")}</button>}
                        </div>
                      : <div key={a.ref} className="rounded-sm border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700" data-testid={`addon-invalid-${a.ref}`}>
                          {t("license.addonInvalid")}: {a.error}
                          {addonEditable && <button onClick={() => removeAddon(a.ref)} className="ml-2 underline" data-testid={`addon-remove-${a.ref}`}>{t("license.remove")}</button>}
                        </div>)
                  : <AddonCard key={a.ref} addon={a} onRemove={removeAddon} editable={addonEditable} t={t} />
              ))}
            </div>
          ) : (
            <p className="mb-4 text-sm text-zinc-400" data-testid="license-addons-empty">{t("license.addonsEmpty")}</p>
          )}
          {addonEditable ? (
            <div className="space-y-2">
              <Textarea
                value={addonInput} onChange={(e) => setAddonInput(e.target.value)}
                placeholder={t("license.addonPlaceholder")} rows={2}
                disabled={addonBusy} data-testid="license-addon-input"
                className="rounded-sm border-zinc-300 font-mono text-xs" />
              <Button onClick={activateAddon} disabled={addonBusy || !addonInput.trim()} data-testid="license-addon-activate-btn" size="sm" className="rounded-sm">
                <Plus size={15} className="mr-1.5" /> {addonBusy ? t("license.activating") : t("license.addonAdd")}
              </Button>
              <p className="text-[11px] text-zinc-400">{t("license.addonHint")}</p>
            </div>
          ) : (
            <div className="rounded-sm border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-800" data-testid="license-addon-managed-note">{demoMode ? t("license.addonDemoNote") : t("license.managed")}</div>
          )}
        </div>
      )}

      {/* Key management (self-hosted only) */}
      <div className="border-t border-zinc-100 p-5">
        {managed ? (
          <div className="rounded-sm border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-800" data-testid="license-managed-note">
            {t("license.managed")}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-zinc-500">
              <KeyRound size={14} /> {t("license.keyTitle")}
            </div>
            <Textarea
              value={keyInput} onChange={(e) => setKeyInput(e.target.value)}
              placeholder={t("license.keyPlaceholder")} rows={3}
              disabled={!editable || busy} data-testid="license-key-input"
              className="rounded-sm border-zinc-300 font-mono text-xs" />
            <div className="flex flex-wrap gap-2">
              <Button onClick={activate} disabled={!editable || busy || !keyInput.trim()} data-testid="license-activate-btn" className="rounded-sm">
                <ShieldCheck size={15} className="mr-1.5" /> {busy ? t("license.activating") : t("license.activate")}
              </Button>
              {lic.keySet && (
                <Button variant="outline" onClick={removeKey} disabled={!editable || busy} data-testid="license-remove-btn" className="rounded-sm border-zinc-300 text-red-600 hover:bg-red-50">
                  <Trash2 size={15} className="mr-1.5" /> {t("license.remove")}
                </Button>
              )}
            </div>
            <p className="text-[11px] text-zinc-400">{t("license.keyHint")}</p>
          </div>
        )}
      </div>

      {/* Build-license policy (self-hosted only) */}
      {!managed && policy && (
        <div className="border-t border-zinc-100 p-5" data-testid="license-policy">
          <div className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-zinc-500">
            <ShieldCheck size={14} /> {t("license.buildCoverage")}
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <label className="text-sm">
              <span className="mb-1 block text-zinc-600">{t("license.graceDays")}</span>
              <input type="number" min={0} max={365} value={policy.graceDays}
                onChange={(e) => setPolicy({ ...policy, graceDays: e.target.value })}
                disabled={!editable || savingPolicy} data-testid="license-grace-days"
                className="w-full rounded-sm border border-zinc-300 px-2 py-1.5 font-mono text-sm" />
            </label>
            <label className="text-sm">
              <span className="mb-1 block text-zinc-600">{t("license.reminderDays")}</span>
              <input type="number" min={0} max={365} value={policy.reminderDays}
                onChange={(e) => setPolicy({ ...policy, reminderDays: e.target.value })}
                disabled={!editable || savingPolicy} data-testid="license-reminder-days"
                className="w-full rounded-sm border border-zinc-300 px-2 py-1.5 font-mono text-sm" />
            </label>
          </div>
          <Button onClick={savePolicy} disabled={!editable || savingPolicy} data-testid="license-policy-save"
            className="mt-3 rounded-sm" size="sm">{t("license.activate")}</Button>
        </div>
      )}
    </section>
  );
};
